import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { contentApi } from "@/lib/api";
import { Loader2 } from "lucide-react";

const EMPLOYEE_RANGES = ["1-50", "51-200", "201-500", "501-1000", "1001-5000", "5000+"];

export const ProfileForm = ({ initial = {}, onSubmit, submitLabel = "Save & continue" }) => {
  const [opts, setOpts] = useState({ industries: [], site_types: [], requested_levels: [] });
  const [form, setForm] = useState({
    company_name: "", website: "", contact_person: "", contact_email: "", country: "",
    industry: "", num_employees: "", site_type: "", customer_industries: "", requested_level: "",
    ...initial,
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => { contentApi.formOptions().then(({ data }) => setOpts(data)); }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try { await onSubmit(form); } finally { setSaving(false); }
  };

  return (
    <form onSubmit={submit} className="space-y-5">
      <div className="grid gap-5 sm:grid-cols-2">
        <Field label="Company name *"><Input required value={form.company_name} onChange={(e) => set("company_name", e.target.value)} data-testid="apply-company-name-input" /></Field>
        <Field label="Website"><Input value={form.website} onChange={(e) => set("website", e.target.value)} placeholder="https://" data-testid="apply-website-input" /></Field>
        <Field label="Contact person *"><Input required value={form.contact_person} onChange={(e) => set("contact_person", e.target.value)} data-testid="apply-contact-person-input" /></Field>
        <Field label="Contact email *"><Input type="email" required value={form.contact_email} onChange={(e) => set("contact_email", e.target.value)} data-testid="apply-contact-email-input" /></Field>
        <Field label="Country *"><Input required value={form.country} onChange={(e) => set("country", e.target.value)} data-testid="apply-country-input" /></Field>
        <Field label="Industry *">
          <Select value={form.industry} onValueChange={(v) => set("industry", v)}>
            <SelectTrigger data-testid="apply-industry-select"><SelectValue placeholder="Select industry" /></SelectTrigger>
            <SelectContent>{opts.industries.map((i) => <SelectItem key={i} value={i}>{i}</SelectItem>)}</SelectContent>
          </Select>
        </Field>
        <Field label="Number of employees *">
          <Select value={form.num_employees} onValueChange={(v) => set("num_employees", v)}>
            <SelectTrigger data-testid="apply-employees-select"><SelectValue placeholder="Select range" /></SelectTrigger>
            <SelectContent>{EMPLOYEE_RANGES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
          </Select>
        </Field>
        <Field label="Facility type *">
          <Select value={form.site_type} onValueChange={(v) => set("site_type", v)}>
            <SelectTrigger data-testid="apply-site-type-select"><SelectValue placeholder="Select type" /></SelectTrigger>
            <SelectContent>{opts.site_types.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select>
        </Field>
        <Field label="Requested certification level *">
          <Select value={form.requested_level} onValueChange={(v) => set("requested_level", v)}>
            <SelectTrigger data-testid="apply-requested-level-select"><SelectValue placeholder="Select level" /></SelectTrigger>
            <SelectContent>{opts.requested_levels.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
          </Select>
        </Field>
      </div>
      <Field label="Major customer industries">
        <Textarea value={form.customer_industries} onChange={(e) => set("customer_industries", e.target.value)} placeholder="e.g. Electronics, Automotive, Retail" data-testid="apply-customer-industries-input" />
      </Field>
      <div className="flex justify-end">
        <Button type="submit" className="btn-green" disabled={saving} data-testid="apply-submit-button">
          {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} {submitLabel}
        </Button>
      </div>
    </form>
  );
};

const Field = ({ label, children }) => (
  <div><Label className="mb-1 block">{label}</Label>{children}</div>
);

export default ProfileForm;
